import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import deliveryChallanService from '../services/deliveryChallanService';

// Async thunks
export const fetchDeliveryChallans = createAsyncThunk(
  'deliveryChallan/fetchDeliveryChallans',
  async (params, { rejectWithValue }) => {
    try {
      const response = await deliveryChallanService.getDeliveryChallans(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch delivery challans');
    }
  }
);

export const createDeliveryChallan = createAsyncThunk(
  'deliveryChallan/createDeliveryChallan',
  async (challanData, { rejectWithValue }) => {
    try {
      const response = await deliveryChallanService.createDeliveryChallan(challanData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create delivery challan');
    }
  }
);

export const updateDeliveryChallan = createAsyncThunk(
  'deliveryChallan/updateDeliveryChallan',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await deliveryChallanService.updateDeliveryChallan(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update delivery challan');
    }
  }
);

export const deleteDeliveryChallan = createAsyncThunk(
  'deliveryChallan/deleteDeliveryChallan',
  async (id, { rejectWithValue }) => {
    try {
      await deliveryChallanService.deleteDeliveryChallan(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete delivery challan');
    }
  }
);

const initialState = {
  list: [],
  current: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

const deliveryChallanSlice = createSlice({
  name: 'deliveryChallan',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentDeliveryChallan: (state, action) => {
      state.current = action.payload;
    },
    clearCurrentDeliveryChallan: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Delivery Challans
      .addCase(fetchDeliveryChallans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryChallans.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.challans;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchDeliveryChallans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Delivery Challan
      .addCase(createDeliveryChallan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDeliveryChallan.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload.challan);
      })
      .addCase(createDeliveryChallan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Delivery Challan
      .addCase(updateDeliveryChallan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDeliveryChallan.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(challan => challan._id === action.payload.challan._id);
        if (index !== -1) {
          state.list[index] = action.payload.challan;
        }
        state.current = action.payload.challan;
      })
      .addCase(updateDeliveryChallan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Delivery Challan
      .addCase(deleteDeliveryChallan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDeliveryChallan.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(challan => challan._id !== action.payload);
      })
      .addCase(deleteDeliveryChallan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentDeliveryChallan, clearCurrentDeliveryChallan } = deliveryChallanSlice.actions;
export default deliveryChallanSlice.reducer;